import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils/cn'
import { type TextSize, textSizeClasses } from '@/lib/utils/text-size-utils'

/**
 * Font weight variants for name display
 */
type FontWeight = 'normal' | 'medium' | 'semibold' | 'bold'

const fontWeightClasses: Record<FontWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
}

/**
 * Display modes for the name component
 */
type DisplayMode = 'text' | 'link' | 'button'

/**
 * Props for the NodeName component
 */
interface nodeNameProps {
  /**
   * The name/title text to display
   */
  name?: string | null
  /**
   * Optional node ID for generating links
   */
  nodeId?: string
  /**
   * Base path for link generation (e.g., '/projects' for projects)
   */
  linkBasePath?: string
  /**
   * Text size variant (defaults to 'sm')
   */
  textSize?: TextSize
  /**
   * Font weight variant (defaults to 'medium')
   */
  fontWeight?: FontWeight
  /**
   * Display mode for the name (defaults to 'text')
   */
  displayMode?: DisplayMode
  /**
   * Additional CSS classes for the container
   */
  className?: string
  /**
   * Whether to truncate long names (defaults to true)
   */
  truncate?: boolean
  /**
   * Maximum width for truncation (defaults to 'max-w-none' for no limit)
   */
  maxWidth?: string
  /**
   * Optional click handler (only used when displayMode is 'button')
   */
  onClick?: () => void
  /**
   * Whether to show the name only when it's not empty (defaults to true)
   */
  showOnlyIfNotEmpty?: boolean
  /**
   * Custom link target (defaults to node detail page)
   */
  linkTarget?: string
  /**
   * Whether the link should open in a new tab
   */
  openInNewTab?: boolean
}

/**
 * Get the appropriate link path based on node type and ID
 */
function getLinkPath(linkBasePath?: string, nodeId?: string): string | undefined {
  if (!linkBasePath || !nodeId) return undefined
  return `${linkBasePath}/${nodeId}`
}

/**
 * A reusable component for displaying node names/titles with consistent styling
 * Supports various display modes including text, links, and buttons with truncation options
 *
 * @example
 * ```tsx
 * // Basic usage - simple text display
 * <NodeName name="Project Alpha" />
 *
 * // With custom styling
 * <NodeName
 *   name="Important Project"
 *   textSize="lg"
 *   fontWeight="bold"
 *   className="text-primary"
 * />
 *
 * // As a clickable link to node detail page
 * <NodeName
 *   name="Project Beta"
 *   nodeId="123"
 *   linkBasePath="/projects"
 *   displayMode="link"
 * />
 *
 * // As a button with click handler
 * <NodeName
 *   name="Project Gamma"
 *   displayMode="button"
 *   onClick={() => handleProjectClick()}
 * />
 *
 * // Without truncation for very long names
 * <NodeName
 *   name="This is a very long project name that should not be truncated"
 *   truncate={false}
 * />
 *
 * // With custom maximum width for truncation
 * <NodeName
 *   name="Project with custom truncation"
 *   maxWidth="max-w-32"
 * />
 *
 * // Link that opens in new tab
 * <NodeName
 *   name="External Project"
 *   nodeId="456"
 *   linkBasePath="/projects"
 *   displayMode="link"
 *   openInNewTab
 * />
 *
 * // Custom link target
 * <NodeName
 *   name="Project Settings"
 *   linkTarget="/projects/789/settings"
 *   displayMode="link"
 * />
 * ```
 */
export function NodeName({
  name,
  nodeId,
  linkBasePath,
  textSize = 'sm',
  fontWeight = 'medium',
  displayMode = 'text',
  className = '',
  truncate = true,
  maxWidth,
  onClick,
  showOnlyIfNotEmpty = true,
  linkTarget,
  openInNewTab = false,
}: nodeNameProps) {
  // Don't render if name is empty/null and showOnlyIfNotEmpty is true
  if (showOnlyIfNotEmpty && (!name || name.trim() === '')) {
    return null
  }

  // Determine the link path
  const linkPath = linkTarget || getLinkPath(linkBasePath, nodeId)

  // Build the className for the text element
  const textClasses = cn(
    textSizeClasses[textSize],
    fontWeightClasses[fontWeight],
    truncate && 'truncate',
    maxWidth,
    className
  )

  // Render based on display mode
  if (displayMode === 'link' && linkPath) {
    return (
      <Link
        to={linkPath}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        className={cn(
          textClasses,
          'hover:underline focus:underline transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
        )}
        title={name || undefined}
      >
        {name}
      </Link>
    )
  }

  if (displayMode === 'button' && onClick) {
    return (
      <button
        onClick={onClick}
        className={cn(
          textClasses,
          'hover:underline focus:underline transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'bg-transparent border-none cursor-pointer text-left p-0'
        )}
        title={name || undefined}
        type="button"
      >
        {name}
      </button>
    )
  }

  // Default text display
  return (
    <span className={textClasses} title={name || undefined}>
      {name}
    </span>
  )
}
